import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockIcon,
  CalendarDaysIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

// Utility for formatting time
const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

// Utility for formatting date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const EmployeeAttendanceDashboard = () => {
  // Mock data for the attendance log
  const initialLog = [
    { id: 1, date: '2025-11-29', checkIn: '2025-11-29T09:00:00', checkOut: '2025-11-29T18:00:00', status: 'Present', duration: '9h 00m' },
    { id: 2, date: '2025-11-28', checkIn: '2025-11-28T09:15:00', checkOut: '2025-11-28T18:30:00', status: 'Present', duration: '9h 15m' },
    { id: 3, date: '2025-11-27', checkIn: '2025-11-27T08:50:00', checkOut: '2025-11-27T17:50:00', status: 'Present', duration: '9h 00m' },
    { id: 4, date: '2025-11-26', checkIn: '2025-11-26T09:05:00', checkOut: '2025-11-26T17:00:00', status: 'Late', duration: '7h 55m' },
    { id: 5, date: '2025-11-25', checkIn: '2025-11-25T08:45:00', checkOut: null, status: 'Absent', duration: 'N/A' },
  ];

  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [attendanceLog, setAttendanceLog] = useState(initialLog);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Simulates a successful location check (e.g., within a geofence)
  const isLocationValid = true; 
  const currentOfficeLocation = 'Main Office - Bangalore';
  const workHoursTarget = '8h 30m'; 

  /**
   * Calculates the duration since clock-in.
   * @returns {string} Formatted duration (e.g., "02h 30m 15s").
   */
  const calculateDuration = () => {
    if (!clockInTime) return '00h 00m 00s';

    const diffMs = currentTime.getTime() - clockInTime.getTime();
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  };

  /**
   * Handles the Clock In action.
   */
  const handleClockIn = () => {
    if (!isLocationValid) {
      // Replaced alert() with console log for safety
      console.error('Cannot Clock In: You are outside the designated Geofence.');
      return;
    }

    const now = new Date();
    setIsClockedIn(true);
    setClockInTime(now);

    // Add a temporary log entry for today
    const today = formatDate(now);
    setAttendanceLog(prevLog => [
      { id: Date.now(), date: today, checkIn: now.toISOString(), checkOut: null, status: 'Pending', duration: 'N/A' },
      ...prevLog.filter(item => item.date !== today) 
    ]);
  };

  /**
   * Handles the Clock Out action.
   */
  const handleClockOut = () => {
    if (!isClockedIn) return;

    const now = new Date();
    const duration = calculateDuration(); 
    
    // Update the state
    setIsClockedIn(false);
    
    // Update the log entry
    setAttendanceLog(prevLog => prevLog.map(item => {
        if (item.checkIn && !item.checkOut && formatDate(item.checkIn) === formatDate(now)) {
            const totalHours = (now.getTime() - new Date(item.checkIn).getTime()) / (1000 * 3600);
            const status = totalHours >= 7 ? 'Present' : 'Short Hours'; 

            return {
                ...item,
                checkOut: now.toISOString(),
                status: status,
                // Remove seconds from duration for log display
                duration: duration.replace(/\s[0-9]{2}s$/, '') 
            };
        }
        return item;
    }));
    
    setClockInTime(null);
  };

  // UI Components
  
  // 1. Attendance Status Card (STABLE: No Framer Motion)
  const AttendanceStatusCard = () => (
    <div 
      className="bg-white p-6 rounded-3xl shadow-xl border border-blue-100 flex flex-col justify-between h-full"
    >
      <div className="flex justify-between items-start">
        <div className="text-left">
          <p className="text-sm font-medium text-gray-500 font-sans tracking-wide">YOUR STATUS</p>
          <h2 className="text-3xl font-extrabold mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {isClockedIn ? 'Clocked In' : 'Clocked Out'}
          </h2>
        </div>
        <ClockIcon className={`h-8 w-8 ${isClockedIn ? 'text-green-500' : 'text-red-500'}`} />
      </div>

      <div className="mt-6 space-y-3">
        {/* Only the content inside changes, not the container, ensuring stability */}
        {isClockedIn && (
            <div className="pt-3 border-t border-gray-100 transition-all duration-300">
                <p className="text-sm text-gray-500 font-sans">Time Since Clock In:</p>
                <p className="text-3xl font-extrabold text-indigo-600 mt-1" style={{ fontFamily: 'Roboto, sans-serif' }}>
                    {calculateDuration()}
                </p>
            </div>
        )}
        <div className="flex items-center text-gray-700 text-sm font-sans">
          <MapPinIcon className="h-5 w-5 text-blue-500 mr-2" />
          <span className="font-semibold">{currentOfficeLocation}</span>
        </div>
      </div>

      <div className="mt-8">
        <button
          onClick={isClockedIn ? handleClockOut : handleClockIn}
          disabled={!isLocationValid}
          className={`w-full flex justify-center items-center py-3 px-4 rounded-xl text-lg font-bold transition duration-300 transform hover:scale-[1.02] 
            ${isClockedIn
              ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-200'
              : 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-200'
            }
            ${!isLocationValid ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {isClockedIn ? (
            <>
              <ArrowRightOnRectangleIcon className="h-6 w-6 mr-2" />
              Clock Out
            </>
          ) : (
            <>
              <ArrowLeftOnRectangleIcon className="h-6 w-6 mr-2" />
              Clock In
            </>
          )}
        </button>
      </div>
      {!isLocationValid && (
        <p className="mt-3 text-red-500 text-xs font-medium flex items-center">
            <ExclamationCircleIcon className="h-4 w-4 mr-1"/> Location check failed. Cannot Clock In.
        </p>
      )}
    </div>
  );

  // 2. Key Metrics Card (STABLE: No Framer Motion)
  const KeyMetricsCard = ({ title, value, icon: Icon, colorClass }) => (
    <div 
      className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 flex items-center justify-between"
    >
      <div>
        <p className="text-sm font-medium text-gray-500 font-sans tracking-wide">{title}</p>
        <p className={`text-2xl font-extrabold mt-1 ${colorClass}`} style={{ fontFamily: 'Roboto, sans-serif' }}>
          {value}
        </p>
      </div>
      <Icon className={`h-8 w-8 ${colorClass}`} />
    </div>
  );
  
  // 3. Current Time Card (STABLE: No Framer Motion)
  const CurrentTimeCard = ({ time, icon: Icon, colorClass }) => (
    <div 
      className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 flex items-center justify-between"
    >
      <div>
        <p className="text-sm font-medium text-gray-500 font-sans tracking-wide">CURRENT TIME</p>
        <p className={`text-2xl font-extrabold mt-1 ${colorClass}`} style={{ fontFamily: 'Roboto, sans-serif' }}>
          {time}
        </p>
      </div>
      <Icon className={`h-8 w-8 ${colorClass}`} />
    </div>
  );


  // 4. Attendance Log (Removed container motion, kept row motion for smooth list updates)
  const AttendanceLog = () => (
    <div 
      className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-xl h-full flex flex-col"
    >
      <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>Attendance Log</h3>
      <div className="overflow-x-auto overflow-y-auto flex-1 max-h-[60vh] md:max-h-[70vh]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-sans">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-sans">Check In</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-sans">Check Out</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-sans hidden sm:table-cell">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-sans">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            <AnimatePresence>
              {attendanceLog.map((log) => {
                const checkInTime = log.checkIn ? formatTime(log.checkIn) : 'N/A';
                const checkOutTime = log.checkOut ? formatTime(log.checkOut) : 'N/A';

                const statusColor = {
                    'Present': 'bg-green-100 text-green-600',
                    'Late': 'bg-yellow-100 text-yellow-600',
                    'Short Hours': 'bg-orange-100 text-orange-600',
                    'Absent': 'bg-red-100 text-red-600',
                    'Pending': 'bg-blue-100 text-blue-600',
                }[log.status] || 'bg-gray-100 text-gray-600';

                return (
                  <motion.tr
                    key={log.id}
                    // Retain row animation for smooth addition/removal from log, which is not a constant update.
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="hover:bg-indigo-50/50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-sans">{formatDate(log.date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-sans">{checkInTime}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-sans">{checkOutTime}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-sans hidden sm:table-cell">{log.duration}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor} font-sans`}>
                        {log.status}
                      </span>
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-gray-500 font-sans">Showing last {attendanceLog.length} attendance records.</p>
    </div>
  );

  // Main Render
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 text-gray-800" style={{ fontFamily: 'Roboto, sans-serif' }}>
      
      {/* Header Section (Retained initial animation for a clean single-time entrance) */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-3xl sm:text-4xl font-extrabold text-indigo-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Employee Attendance Dashboard 
        </h1>
        <p className="text-md sm:text-xl font-light text-indigo-600 mt-2 font-sans">
          Welcome back! Track your time and view your attendance log.
        </p>
        <p className="mt-3 text-xl font-bold text-gray-800">
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Key Metrics Row (All Standard DIVs) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* 1. CURRENT TIME - STABLE */}
          <CurrentTimeCard
            time={currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
            icon={ClockIcon} 
            colorClass="text-indigo-600"
          />

          {/* 2. WORK TARGET - STABLE */}
          <KeyMetricsCard 
            title="WORK TARGET" 
            value={workHoursTarget} 
            icon={CalendarDaysIcon} 
            colorClass="text-blue-600"
          />
          
          {/* 3. TOTAL PRESENCE - STABLE */}
          <KeyMetricsCard 
            title="TOTAL PRESENCE" 
            value="3/5 Days" 
            icon={CheckCircleIcon} 
            colorClass="text-green-600"
          />
          
          {/* 4. LATE ARRIVALS - STABLE */}
          <KeyMetricsCard 
            title="LATE ARRIVALS" 
            value="1 Day" 
            icon={XCircleIcon} 
            colorClass="text-red-600"
          />
        </div>

        {/* Status and Log Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Geofence Status / Clocking Card (LHS) - STABLE */}
          <div className="lg:col-span-1">
            <AttendanceStatusCard />
          </div>

          {/* Attendance Log (RHS) - STABLE container */}
          {AttendanceLog()}
        </div>
        
      </div>
    </div>
  );
};

export default EmployeeAttendanceDashboard;